import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where, addDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { S, C } from "../ui";

const TYPES = ["missed_checklist", "no_show", "late_clockout", "complaint"];
export const TYPE_LABEL = { missed_checklist: "Missed checklist", no_show: "No-show", late_clockout: "Late clock-out", complaint: "Complaint" };

// Facility Admin only (spec: "lets facility admins log accountability
// issues... against any vendor/worker") — see firestore.rules.
export default function IncidentsPanel({ facilityId, vendors }) {
  const [incidents, setIncidents] = useState([]);
  const [adding, setAdding] = useState(false);
  const [vendorId, setVendorId] = useState(""); // "" = internal staff
  const [type, setType] = useState(TYPES[0]);
  const [description, setDescription] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => onSnapshot(query(collection(db, "incidentFlags"), where("facilityId", "==", facilityId)), s =>
    setIncidents(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)))), [facilityId]);

  const vendorName = (vId) => vId ? (vendors.find(v => v.id === vId)?.companyName || "Unknown vendor") : "Internal Staff";

  const logIncident = async () => {
    if (!description.trim()) { setErr("Enter a description."); return; }
    setErr("");
    try {
      await addDoc(collection(db, "incidentFlags"), {
        facilityId, vendorId: vendorId || null, type, description: description.trim(),
        timestamp: serverTimestamp(), resolved: false,
      });
      setDescription(""); setAdding(false);
    } catch (e) {
      setErr("Couldn't log incident — try again.");
    }
  };

  const toggleResolved = (incident) => updateDoc(doc(db, "incidentFlags", incident.id), { resolved: !incident.resolved });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.muted }}>Accountability issues against any vendor or internal staff.</div>
        <button style={{ ...S.btn(), width: "auto", padding: "10px 18px" }} onClick={() => setAdding(a => !a)}>{adding ? "Cancel" : "+ Log Incident"}</button>
      </div>

      {adding && (
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 0, padding: 16, marginBottom: 20, maxWidth: 420 }}>
          {err && <div style={S.err}>{err}</div>}
          <select style={{ ...S.input, background: C.white }} value={vendorId} onChange={e => setVendorId(e.target.value)}>
            <option value="">Internal Staff</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.companyName}</option>)}
          </select>
          <select style={{ ...S.input, background: C.white }} value={type} onChange={e => setType(e.target.value)}>
            {TYPES.map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
          </select>
          <textarea style={{ ...S.input, minHeight: 80 }} placeholder="What happened?" value={description} onChange={e => setDescription(e.target.value)} />
          <button style={S.btn(C.red)} onClick={logIncident}>Log Incident</button>
        </div>
      )}

      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {incidents.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>No incidents logged.</div>
        )}
        {incidents.map((inc, idx) => (
          <div key={inc.id} style={{ padding: "14px 16px", borderBottom: idx === incidents.length - 1 ? "none" : `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                <span style={S.pill(inc.resolved ? C.tintSuccess : C.tintDanger, inc.resolved ? C.success : C.red)}>{TYPE_LABEL[inc.type] || inc.type}</span>
                {" "}{vendorName(inc.vendorId)}
              </div>
              <div style={{ fontSize: 13, color: C.muted }}>{inc.description}</div>
            </div>
            <button onClick={() => toggleResolved(inc)}
              style={{ ...S.ghostBtn, width: "auto", padding: "6px 12px", whiteSpace: "nowrap", color: inc.resolved ? C.muted : C.green, borderColor: inc.resolved ? C.border : C.green }}>
              {inc.resolved ? "Resolved" : "Mark Resolved"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
